import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Eraser } from "lucide-react";

export type SignaturePadHandle = {
  /** The drawing as a PNG data URL, or `null` while nothing has been drawn. */
  toDataURL: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
};

interface SignaturePadProps {
  /** Accessible name of the drawing area (WCAG 4.1.2 — a canvas has no text). */
  label: string;
  clearLabel: string;
  hint?: string;
  disabled?: boolean;
  onChange?: (hasSignature: boolean) => void;
}

// Paper, and ink: the signature is drawn dark on white in both themes, because
// what leaves this component is a picture that ends up on a white page. Ink
// that followed the theme would sign the PDF in white on white.
const INK = "#111827";
const PAPER = "#ffffff";
const STROKE_WIDTH = 2;

/**
 * Where the patient draws their signature, with a pointer or a fingertip.
 *
 * The page asks for the drawing (`toDataURL`) rather than being handed every
 * stroke: what matters upstream is whether there is a signature and what it
 * looks like, not how it was made.
 *
 * Drawing is a pointer-only act, so it is never the single way to consent —
 * the page keeps an acceptance that needs no drawing for anybody who cannot
 * make one.
 */
const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { label, clearLabel, hint, disabled = false, onChange },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const hasSignatureRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(canvas.clientWidth, 1);
    const height = Math.max(canvas.clientHeight, 1);
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    context.scale(ratio, ratio);
    context.fillStyle = PAPER;
    context.fillRect(0, 0, width, height);
    context.lineWidth = STROKE_WIDTH;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = INK;
    contextRef.current = context;
  }, []);

  const announce = useCallback(
    (next: boolean) => {
      if (hasSignatureRef.current === next) return;
      hasSignatureRef.current = next;
      setHasSignature(next);
      onChange?.(next);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      paint();
    }
    announce(false);
  }, [announce, paint]);

  useEffect(() => {
    paint();
    // Resizing resets the bitmap, so a pad that already holds a signature is
    // left alone: a patient does not lose what they drew because the keyboard
    // opened and the viewport changed under them.
    const onResize = () => {
      if (!hasSignatureRef.current) paint();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [paint]);

  useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => !hasSignatureRef.current,
      clear,
      toDataURL: () =>
        hasSignatureRef.current && canvasRef.current
          ? canvasRef.current.toDataURL("image/png")
          : null,
    }),
    [clear],
  );

  const pointAt = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const startStroke = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const context = contextRef.current;
    if (disabled || !context) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { x, y } = pointAt(event);
    drawingRef.current = true;
    context.beginPath();
    context.moveTo(x, y);
    // A tap is a signature too — draw the dot instead of waiting for a move.
    context.lineTo(x, y);
    context.stroke();
    announce(true);
  };

  const extendStroke = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const context = contextRef.current;
    if (disabled || !drawingRef.current || !context) return;
    const { x, y } = pointAt(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const endStroke = () => {
    drawingRef.current = false;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label}
          aria-disabled={disabled || undefined}
          className={`w-full h-40 rounded-xl border border-gray-300 dark:border-gray-600 bg-white ${
            disabled ? "opacity-60" : "cursor-crosshair"
          }`}
          style={{ touchAction: "none" }}
          onPointerDown={startStroke}
          onPointerMove={extendStroke}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-6 bottom-8 border-b border-dashed border-gray-300"
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        {hint ? <p className="text-xs text-content-tertiary">{hint}</p> : <span />}
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasSignature}
          className="flex items-center gap-1.5 min-h-11 px-2 text-xs font-medium text-content-secondary hover:text-content-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Eraser className="w-3.5 h-3.5" />
          {clearLabel}
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
